import {
  forwardRef,
  useId,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { cn } from "../utils/cn";
import { Label } from "./Label";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  helper?: ReactNode;
  required?: boolean;
}

const BASE =
  "w-full rounded-md border bg-neutral-0 px-4 py-3 text-sm leading-relaxed text-text-primary outline-none transition-shadow resize-y min-h-22 " +
  "focus-visible:ring-2 focus-visible:ring-primary-500/40";

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, helper, required, className, id, rows = 4, ...rest },
  ref,
): ReactElement {
  const autoId = useId();
  const taId = id ?? autoId;
  const errorId = `${taId}-error`;
  const helperId = `${taId}-helper`;
  const describedBy = error ? errorId : helper ? helperId : undefined;

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={taId} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <textarea
        ref={ref}
        id={taId}
        rows={rows}
        required={required}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        className={cn(
          BASE,
          error ? "border-danger-500 focus-visible:ring-danger-500/30" : "border-neutral-300",
          className,
        )}
        {...rest}
      />
      {error ? (
        <p id={errorId} className="mt-2 text-xs text-danger-600">
          {error}
        </p>
      ) : helper ? (
        <p id={helperId} className="mt-2 text-xs text-text-tertiary">
          {helper}
        </p>
      ) : null}
    </div>
  );
});
